<template>
    <CustomDialog
        :value="true"
        :default-title="$t('codeModal.title')"
        width="640px"
        height-class="ppv2-max-h-[90vh]"
        @input="closeModal"
        @cancel="closeModal"
    >
        <template #title>
            <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                <div
                    class="ppv2-flex ppv2-h-11 ppv2-w-11 ppv2-items-center ppv2-justify-center ppv2-rounded-xl ppv2-bg-[#7D2DBD] ppv2-text-white"
                >
                    <component :is="selectedWidget.iconName" :size="22" :stroke-width="2" />
                </div>
                <div class="ppv2-flex-1 ppv2-min-w-0">
                    <div class="ppv2-text-lg poppinsSemiBold ppv2-text-black">{{ $t('codeModal.integrationCode') }}</div>
                    <div class="ppv2-text-sm ppv2-text-black ppv2-opacity-80">
                        {{ selectedWidget.title }}
                    </div>
                </div>
            </div>
        </template>

        <template #content>
            <div class="ppv2-flex ppv2-flex-col ppv2-gap-5 ppv2-p-4">
                <InfoBanner variant="info" :customFontSize="12">
                    {{ $t('codeModal.infoBanner') }}
                </InfoBanner>

                <CookieManagementToggle v-model="cookieConsent" :widget-id="widgetId" :in-modal="true" />

                <div
                    class="ppv2-rounded-2xl ppv2-bg-[#0B112C] ppv2-text-gray-300 ppv2-overflow-hidden ppv2-border ppv2-border-[#1E264A]"
                >
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-bg-[#0E1637] ppv2-px-4 ppv2-py-3 ppv2-border-b ppv2-border-[#1E264A]/50"
                    >
                        <div
                            class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-text-xs ppv2-font-mono ppv2-tracking-wide ppv2-text-gray-400"
                        >
                            <span>&lt;/&gt;</span>
                            <span>{{ $t('codeModal.codeToPaste') }}</span>
                        </div>
                        <div class="ppv2-flex ppv2-gap-1.5">
                            <span class="ppv2-h-2.5 ppv2-w-2.5 ppv2-rounded-full ppv2-bg-[#FF5F56]"></span>
                            <span class="ppv2-h-2.5 ppv2-w-2.5 ppv2-rounded-full ppv2-bg-[#FFBD2E]"></span>
                            <span class="ppv2-h-2.5 ppv2-w-2.5 ppv2-rounded-full ppv2-bg-[#27C93F]"></span>
                        </div>
                    </div>
                    <div
                        v-text="displaySnippet"
                        class="ppv2-p-4 ppv2-max-h-[180px] ppv2-overflow-y-auto ppv2-overflow-x-auto ppv2-font-mono ppv2-text-xs md:ppv2-text-sm ppv2-leading-relaxed ppv2-whitespace-pre ppv2-text-left ppv2-select-all"
                    ></div>
                </div>
            </div>
        </template>

        <template #actions>
            <BaseButton
                variant="primary"
                @click="copyToClipboard"
                :customHeight="48"
                customClass="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2 ppv2-shadow-lg ppv2-transition"
            >
                <Check />
                <span>{{ copyButtonText }}</span>
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import InfoBanner from '../../Shared/InfoBanner.vue'
import CookieManagementToggle from './CookieManagementToggle.vue'
import {
    LayoutGrid,
    Layers,
    Gift,
    Calendar,
    CreditCard,
    RefreshCw,
    Tag,
    ShoppingCart,
    FileText,
    Utensils,
    ArrowRight,
    Sparkles,
    Check,
    Ticket,
    User,
} from 'lucide-vue'

export default {
    components: {
        CustomDialog,
        BaseButton,
        InfoBanner,
        LayoutGrid,
        Layers,
        Gift,
        Calendar,
        CreditCard,
        RefreshCw,
        Tag,
        ShoppingCart,
        FileText,
        Utensils,
        ArrowRight,
        Sparkles,
        Ticket,
        User,
        CookieManagementToggle,
        Check,
    },
    props: {
        selectedWidget: {
            type: Object,
            required: true,
        },
        codeSnippet: {
            type: String,
            required: true,
        },
        widgetId: {
            type: String,
            default: '',
        },
        base_url: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            cookieConsent: false,
            copyButtonText: this.$t('codeModal.copyCode'),
        }
    },
    computed: {
        displaySnippet() {
            if (!this.codeSnippet || this.codeSnippet.includes('<iframe')) {
                return this.codeSnippet
            }

            const dynamicEventBlock = !this.cookieConsent
                ? `    // Aucune bannière cookies : le widget se charge directement.\n    window.dispatchEvent(new CustomEvent("playpro:no-consent"));`
                : `    // Remplacez ces valeurs selon l'état réel de votre bannière (CMP)\n    window.dispatchEvent(new CustomEvent("playpro:consent", {\n      detail: {\n        accepted: ["necessary", "measurement"],\n        rejected: ["marketing", "advertisement"]\n      }\n    }));`

            return `${this.codeSnippet}\n<script>\n  window.addEventListener("playpro:ready", function () {\n${dynamicEventBlock}\n  });\n<\\/script>`
        },
    },
    methods: {
        closeModal() {
            this.$emit('close')
        },
        copyToClipboard() {
            const text = this.displaySnippet
            const succeed = () => {
                this.$emit('copied', this.displaySnippet)
                this.$emit('close')
            }
            const fail = () => {
                this.copyButtonText = this.$t('codeModal.copyError')
            }

            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(succeed).catch(fail)
            } else {
                const textarea = document.createElement('textarea')
                textarea.value = text
                textarea.style.position = 'fixed'
                textarea.style.opacity = '0'
                document.body.appendChild(textarea)
                textarea.focus()
                textarea.select()
                try {
                    document.execCommand('copy')
                    succeed()
                } catch (err) {
                    fail()
                } finally {
                    document.body.removeChild(textarea)
                }
            }
        },
    },
}
</script>
